/**
 * Client-side mirror of the employee-feed field mapping defaults
 * (customer-integrations packages/handlers/employee/src/mapping.ts DEFAULT_MAPPINGS).
 * Powers the AEF setup wizard's "drop a sample file, auto-detect the columns"
 * step so a CSR sees exactly what the pipeline would map. Keep this in lockstep
 * with the pipeline's DEFAULT_MAPPINGS — same as classifyPreview.js does for the
 * filename classifier.
 *
 * PROTOTYPE NOTE: for production, replace this ported copy with a server-side
 * "detect" call into the pipeline's own mapping compiler, so the two can never
 * drift (the same discipline as dry-run-before-live).
 */

// Header ladders per ERIN AccountClaim field: first header present (trimmed,
// exact) wins. Ported verbatim from mapping.ts DEFAULT_MAPPINGS.
export const FIELD_LADDERS = {
	firstName: [
		'firstName', 'First Name', 'FirstName', 'First_Name', 'FIRST_NAME', 'GivenName', 'Legal_Name',
		'Legal_Firstname', 'Legal Name - First Name', 'Employee First Name', 'Preferred_First_Name',
		'Preferred Name - First Name',
	],
	lastName: [
		'lastName', 'Last', 'Last Name', 'LastName', 'Last_Name', 'LAST_NAME', 'Legal_Lastname',
		'Legal Name - Last Name', 'Legal_Name_-_Last_Name', 'Preferred_Last_Name',
		'Preferred Name - Last Name', 'Surname', 'Employee Last Name',
	],
	middleName: ['MiddleName', 'Middle Name'],
	fullName: ['Preferred Name', 'EMPLOYEE_NAME', 'Employee Name', 'Full Name', 'Full Name: First Last', 'EE Name'],
	emailAddress: [
		'emailAddress', 'adp-EMAILADDRESS', 'BUSN Email', 'Current Work Email', 'Current Home Email',
		'Email Address', 'EmailAddress', 'email_address', 'EMAIL', 'Email', 'email',
		'Email - Primary Work', 'Employee Email', 'Employee_Email', 'Personal Email', 'Personal_Email',
		'Primary Email', 'Primary_Work_Email', 'primaryWorkEmail', 'Work Email', 'WORK_EMAIL',
		'Work_Email', 'workEmail', 'Email Address Work', 'Business Email',
	],
	alternateEmailAddress: ['EMAIL_ADDRESS2', 'Alternate Email', 'Alternative Email'],
	employeeId: [
		'Employee Number', 'EmployeeNumber', 'Employee_Number', 'Employee ID', 'employeeID',
		'employeeId', 'EmployeeID', 'EMPLOYEEID', 'Employee #', 'EE #', 'EE#', 'DrvID', 'Employee_ID',
		'ADP_POSITIONID', 'EMPLID', 'Employee_Code', 'Employee Identifier',
	],
	atsId: [
		'ATS ID', 'ATS_ID', 'ATSID', 'Candidate ID', 'Candidate_ID', 'Candidate Id', 'candidateID',
		'CF_LRV_Greenhouse_Applicant_ID', 'Greenhouse ID', 'iCIMS ID', 'iCIMSID', 'System ID', 'Taleo_AMS',
	],
	phoneNumber: ['Phone', 'PhoneNumber', 'Home Phone', 'HomePhone', 'Phone - Primary Work', 'primaryWorkPhone'],
	alternatePhoneNumber: ['CellPhone'],
	title: [
		'EmployeeTitle', 'Business Title', 'Occupation', 'Job', 'Job (Alternate)', 'Job Title',
		'JOB_TITLE', 'Job_Title', 'JobTitle', 'jobTitle', 'Job Title (PIT)', 'Position',
		'Position Name', 'Title', 'Custom Field 1',
	],
	department: [
		'BU', 'DEPARTMENT', 'Department', 'department', 'Department Name', 'Department_Name',
		'Dept Desc', 'BUS Node', 'Cost Center', 'DISTRICT', 'Team Name', 'Business Line + Function',
		'Custom Field 1', 'Org Level 1 Code',
	],
	departmentCode: ['Department Code'],
	branchLocation: [
		'BranchLocation', 'COUNTRY', 'Country', 'country', 'Country_of_Citizenship',
		'CF_-_LRV_-_Country_ISO_Code', 'LOCATION', 'Location', 'Location Description',
		'Location Name', 'Work Location Description',
	],
	subCompany: [
		'Sector', 'Subcompany', 'subCompany', 'Subcompany/ Community name', 'Teck Coal Limited',
		'Company Ministry', 'Work_Location_Description', 'Custom Field 2', 'Business_Unit',
		'Sub-Company', 'Market : Name',
	],
	lastHireDate: [
		'Hire Date', 'HireDate', 'Hire_Date', 'hireDate', 'Last Hire Date', 'LastHireDate', 'Rehire Dt',
	],
	startDate: ['Start Date', 'StartDate', 'Start_Date', 'Position Start Date'],
	dateOfBirth: [
		'dateOfBirth', 'Birth Date', 'BirthDate', 'Birth_Date_(MM/DD/YYYY)', 'Date of Birth',
		'Date Of Birth', 'Date_Of_Birth', 'DateOfBirth', 'DOB',
	],
	managerRole: ['Manager Role'],
	role: ['Admin Role', 'admin role'],
	EEOCategory: [
		'EEOCategory', 'Employee Type', 'EmployeeType', 'FT/PT', 'Pay Group', 'Full_Time_Part_Time',
		'Employee_Type', 'EMPLOYEE_TYPE', 'EE Status', 'ASSIGNMENT_CATEGORY', 'DOL_Status', 'TimeType',
		'Pay Class', 'Pay Class Name', 'Employment Type', 'Type',
	],
	serviceAnniversaryDate: ['Service Anniversary Date', 'ServiceAnniversaryDate'],
	positionNumber: ['Position Number'],
};

/**
 * Status is special: an ordered ladder of {field, activeValues?, inactiveValues?}.
 * First column present wins; a value is inactive if it's in inactiveValues, else
 * active (unless activeValues is set, in which case only those are active).
 */
export const STATUS_LADDER = [
	{ field: 'Status', inactiveValues: ['Terminated', 'Inactive', 'TERMINATED'], activeValues: ['Active', 'ACTIVE'] },
	{ field: 'Active Status', activeValues: ['Yes'] },
	{ field: 'EmployeeStatus', inactiveValues: ['On Leave'] },
	{ field: 'employeeStatus', inactiveValues: ['TERMINATED'] },
	{ field: 'Emp Status', inactiveValues: ['T'] },
	{ field: 'Employment Status Code', inactiveValues: ['L'] },
];

// The fields a CSR can hand-assign a column to, grouped for the dropdown. Core
// first (these are what an AccountClaim actually needs), then the long tail.
export const ASSIGNABLE_FIELDS = [
	{ value: 'firstName', label: 'First Name', core: true },
	{ value: 'lastName', label: 'Last Name', core: true },
	{ value: 'emailAddress', label: 'Email Address', core: true },
	{ value: 'employeeId', label: 'Employee ID (Match Key)', core: true },
	{ value: 'title', label: 'Job Title', core: true },
	{ value: 'department', label: 'Department', core: true },
	{ value: 'phoneNumber', label: 'Phone', core: true },
	{ value: 'status', label: 'Employment Status', core: true },
	{ value: 'atsId', label: 'ATS ID' },
	{ value: 'middleName', label: 'Middle Name' },
	{ value: 'fullName', label: 'Full Name' },
	{ value: 'alternateEmailAddress', label: 'Alternate Email' },
	{ value: 'alternatePhoneNumber', label: 'Alternate Phone' },
	{ value: 'departmentCode', label: 'Department Code' },
	{ value: 'branchLocation', label: 'Branch / Location' },
	{ value: 'subCompany', label: 'Sub-Company' },
	{ value: 'lastHireDate', label: 'Hire Date' },
	{ value: 'startDate', label: 'Start Date' },
	{ value: 'dateOfBirth', label: 'Date of Birth' },
	{ value: 'EEOCategory', label: 'Employee Type / EEO' },
];

/**
 * Headers that aren't in the default ladders but almost always mean a specific
 * field. The wizard PRE-SELECTS these (as a promotion, so an override is emitted
 * and the pipeline honors it — NOT a silent auto-map, which would break parity
 * with the pipeline's own ladders). Applied only when the target field isn't
 * already claimed by a real default, one column per field. Promote to a real
 * shared default later via a reviewed pipeline change.
 */
export const SUGGESTED_PROMOTIONS = {
	'Work Phone': 'phoneNumber',
	'Original Hire Date': 'lastHireDate',
};

// Fields we warn about if the file has no column for them — an AccountClaim is
// weak without a name, a contact email, and a stable match key.
export const IMPORTANT_FIELDS = ['firstName', 'lastName', 'emailAddress', 'employeeId'];

export const fieldLabel = (field) =>
	ASSIGNABLE_FIELDS.find((f) => f.value === field)?.label || field;

const norm = (header) => String(header ?? '').trim();

/**
 * Field-first detection (matches how the pipeline resolves each field against
 * the file's columns). Returns, per ERIN field, the first ladder header present.
 * @returns {Record<string,string>} field -> matched column header
 */
export function detectFieldMappings(headers) {
	const present = new Set((headers || []).map(norm));
	const result = {};
	for (const [field, ladder] of Object.entries(FIELD_LADDERS)) {
		const hit = ladder.find((entry) => present.has(entry));
		if (hit) result[field] = hit;
	}
	const statusHit = STATUS_LADDER.find((s) => present.has(s.field));
	if (statusHit) result.status = statusHit.field;
	return result;
}

/**
 * Column-centric view for the wizard table: each file column, the ERIN field it
 * auto-mapped to (or null → falls to a custom value), so a CSR can promote the
 * ones that should be first-class fields (e.g. "Work Phone" → phone).
 *
 * @returns {{ columns: Array<{column, autoField}>, missingImportant: string[], statusColumn: string|null }}
 */
export function autoDetectMappings(headers) {
	const trimmed = (headers || []).map(norm).filter(Boolean);
	const fieldMappings = detectFieldMappings(trimmed);
	// Invert: which column each field consumed (first field to claim a column wins
	// the label; a column can still feed customValues downstream).
	const columnToField = new Map();
	for (const [field, column] of Object.entries(fieldMappings)) {
		if (!columnToField.has(column)) columnToField.set(column, field);
	}
	const columns = trimmed.map((column) => ({
		column,
		autoField: columnToField.get(column) || null,
	}));
	const missingImportant = IMPORTANT_FIELDS.filter((f) => !fieldMappings[f]);
	return { columns, missingImportant, statusColumn: fieldMappings.status || null };
}

/**
 * Initial column -> field assignments: auto-mapped columns keep their field,
 * known suggestions (SUGGESTED_PROMOTIONS) are pre-selected when their target
 * isn't already taken, everything else defaults to a captured custom value.
 * @returns {{ assignments: Record<string,string>, suggested: Set<string> }}
 */
export function seedAssignments(columns) {
	const claimed = new Set(columns.filter((c) => c.autoField).map((c) => c.autoField));
	const assignments = {};
	const suggested = new Set();
	for (const { column, autoField } of columns) {
		if (autoField) {
			assignments[column] = autoField;
			continue;
		}
		const suggestion = SUGGESTED_PROMOTIONS[column];
		if (suggestion && !claimed.has(suggestion)) {
			assignments[column] = suggestion;
			claimed.add(suggestion);
			suggested.add(column);
		} else {
			assignments[column] = CUSTOM_VALUE;
		}
	}
	return { assignments, suggested };
}

/**
 * Interpret a status column's distinct values using the default ladder, so the
 * wizard can show "A → active, L → inactive" and let the CSR correct it.
 * @returns {Array<{ value, active }>}
 */
export function interpretStatusValues(statusColumn, distinctValues) {
	const rule = STATUS_LADDER.find((s) => s.field === statusColumn);
	return (distinctValues || []).map((value) => {
		if (!rule) return { value, active: true };
		const v = String(value ?? '').trim();
		if (rule.activeValues) return { value, active: rule.activeValues.includes(v) };
		if (rule.inactiveValues) return { value, active: !rule.inactiveValues.includes(v) };
		return { value, active: true };
	});
}

// Sentinel: leave a column as a captured custom value (the pipeline auto-captures
// any unmapped column into customValues, so this needs no doc entry — same as the
// legacy EC2 AEF).
export const CUSTOM_VALUE = '__custom__';

/**
 * Build the minimal per-company mapping doc from the wizard's field rules.
 * `fieldSpecs` is { field -> uiSpec }. A field whose rule is a plain column equal
 * to the default-detected column is omitted (the default ladder already yields
 * it); everything else emits its FieldSpec. Unmapped columns are captured as
 * custom values by the pipeline automatically (no doc entry).
 */
export function buildMappingDoc({ companyId, fieldSpecs, autoFieldColumns, absentClaimAction, statusRules, note }) {
	const fields = {};
	for (const [field, spec] of Object.entries(fieldSpecs || {})) {
		const json = buildFieldSpecJson(spec);
		if (!json) continue;
		// Omit when the default ladder already produces this exact mapping.
		if (json.column && autoFieldColumns?.[field] === json.column) continue;
		fields[field] = json;
	}
	const doc = { companyId, taskType: 'employee' };
	if (note) doc.note = note;
	if (Object.keys(fields).length) doc.fields = fields;
	const policy = {};
	if (absentClaimAction) policy.absentClaimAction = absentClaimAction;
	if (Object.keys(policy).length) doc.policy = policy;
	if (statusRules) doc.statusRules = statusRules;
	return doc;
}

// ── Value-based suggestions ──────────────────────────────────────────────────
// Which ERIN field a reference set implies. A column whose VALUES look like the
// company's stored departments should map to `department`, etc. (These are the
// same lists the pipeline resolves against with findItemByNameAndKeywords, so a
// match previews what the import will actually do.)
export const REFERENCE_FIELD = {
	departments: 'department',
	subCompanies: 'subCompany',
	userGroups: 'EEOCategory',
};

const normRef = (value) => String(value ?? '').trim().toLowerCase();

/**
 * Build a normalized lookup Set from a company's reference items (name +
 * keywords), for value matching. `keywords` may be an array or a delimited string.
 */
export function toReferenceSet(items) {
	const set = new Set();
	for (const item of items || []) {
		const name = normRef(item?.name);
		if (name) set.add(name);
		const kw = item?.keywords;
		if (Array.isArray(kw)) for (const k of kw) { const n = normRef(k); if (n) set.add(n); }
		else if (typeof kw === 'string') for (const k of kw.split(/[,;|]/)) { const n = normRef(k); if (n) set.add(n); }
	}
	return set;
}

/**
 * Given a column's distinct values and the company's reference sets
 * ({ departments, subCompanies, userGroups } of normalized names), return the
 * best-matching field suggestion, or null. Requires ≥2 matches and ≥50% overlap.
 * @returns {null | { field, refKey, matched, total, ratio }}
 */
export function suggestFieldByValues(distinctValues, referenceSets) {
	const values = (distinctValues || []).map(normRef).filter(Boolean);
	if (values.length < 2) return null;
	let best = null;
	for (const [refKey, set] of Object.entries(referenceSets || {})) {
		if (!set || !set.size) continue;
		let matched = 0;
		for (const v of values) if (set.has(v)) matched += 1;
		const ratio = matched / values.length;
		if (matched >= 2 && ratio >= 0.5 && (!best || ratio > best.ratio)) {
			best = { field: REFERENCE_FIELD[refKey], refKey, matched, total: values.length, ratio };
		}
	}
	return best;
}

// ── Field rules (columns / template / equalsMap / constant / none) ────────────
// The wizard configures a per-field rule; buildFieldSpecJson turns it into the
// pipeline's FieldSpec. These mirror mappingSpec.ts — keep them in lockstep.

export const RULE_TYPES = [
	{ value: 'column', label: 'Single column' },
	{ value: 'coalesce', label: 'Coalesce — first non-empty of several' },
	{ value: 'template', label: 'Template — combine columns' },
	{ value: 'equalsMap', label: 'Value map — translate values' },
	{ value: 'constant', label: 'Constant — same value every row' },
	{ value: 'none', label: 'Off — disable this field' },
];

// Transform pipes, ported from mappingSpec.ts TRANSFORMS (approximate title/name
// splitting is fine for the preview; the pipeline is the source of truth on write).
const splitNameParts = (value) => {
	const s = String(value ?? '').trim();
	if (s.includes(',')) {
		const [last, first] = s.split(',');
		return { firstName: (first || '').trim(), lastName: (last || '').trim() };
	}
	const parts = s.split(/\s+/).filter(Boolean);
	if (parts.length <= 1) return { firstName: parts[0] || '', lastName: '' };
	return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

export const TRANSFORM_FNS = {
	lower: (v) => v.toLowerCase(),
	upper: (v) => v.toUpperCase(),
	trim: (v) => v.trim(),
	lowerTrim: (v) => v.trim().toLowerCase(),
	title: (v) => v.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()),
	stripNonAlnum: (v) => v.replace(/[^a-zA-Z0-9]/g, ''),
	stripNonAlpha: (v) => v.replace(/[^a-zA-Z]/g, ''),
	digits: (v) => v.replace(/\D/g, ''),
	first1: (v) => v.slice(0, 1),
	first4: (v) => v.slice(0, 4),
	first7: (v) => v.slice(0, 7),
	afterLastDash: (v) => v.split('-').pop() ?? '',
	removeFirstSpace: (v) => v.replace(' ', ''),
	stripLeadingZeros: (v) => v.replace(/^0+/, ''),
	splitNameFirst: (v) => splitNameParts(v).firstName,
	splitNameLast: (v) => splitNameParts(v).lastName,
};

export const TRANSFORM_OPTIONS = [
	{ value: 'lowerTrim', label: 'lowercase + trim' },
	{ value: 'lower', label: 'lowercase' },
	{ value: 'upper', label: 'UPPERCASE' },
	{ value: 'trim', label: 'trim spaces' },
	{ value: 'title', label: 'Title Case' },
	{ value: 'digits', label: 'digits only' },
	{ value: 'stripLeadingZeros', label: 'strip leading zeros' },
	{ value: 'stripNonAlnum', label: 'letters + digits only' },
	{ value: 'stripNonAlpha', label: 'letters only' },
	{ value: 'first1', label: 'first character' },
	{ value: 'first4', label: 'first 4 characters' },
	{ value: 'first7', label: 'first 7 characters' },
	{ value: 'afterLastDash', label: 'after last dash' },
	{ value: 'removeFirstSpace', label: 'remove first space' },
	{ value: 'splitNameFirst', label: 'first name (from full name)' },
	{ value: 'splitNameLast', label: 'last name (from full name)' },
];

const applyTransformChain = (value, chain) => {
	let out = value;
	for (const name of chain || []) {
		const fn = TRANSFORM_FNS[name];
		if (fn) out = fn(out);
	}
	return out;
};

const TEMPLATE_PLACEHOLDER = /\{([^{}|]+)((?:\|[^{}|]+)*)\}/g;

/** Turn a wizard UI rule into the pipeline's FieldSpec JSON (or null to skip). */
export function buildFieldSpecJson(spec) {
	if (!spec) return null;
	switch (spec.type) {
		case 'column': {
			if (!spec.column) return null;
			const chain = (spec.transforms || []).filter(Boolean);
			if (!chain.length) return { column: spec.column };
			// A single column with transforms is expressed as a one-placeholder template.
			return { template: `{${spec.column}|${chain.join('|')}}` };
		}
		case 'coalesce': {
			const cols = (spec.columns || []).filter(Boolean);
			if (!cols.length) return null;
			return cols.length === 1 ? { column: cols[0] } : { columns: cols };
		}
		case 'template': {
			if (!spec.template) return null;
			const out = { template: spec.template };
			if (spec.requireAll) out.requireAll = true;
			return out;
		}
		case 'equalsMap': {
			if (!spec.column) return null;
			const cases = {};
			for (const c of spec.cases || []) if (c && c.value !== '' && c.value != null) cases[c.value] = c.result ?? '';
			const equalsMap = { column: spec.column, cases };
			if (spec.default) equalsMap.default = spec.default;
			if (spec.transform) equalsMap.transform = spec.transform;
			return { equalsMap };
		}
		case 'constant':
			return spec.constant === '' || spec.constant == null ? null : { constant: spec.constant };
		case 'none':
			return { none: true };
		default:
			return null;
	}
}

/** Columns a set of field rules reads (for leftover→custom-value detection). */
export function consumedColumns(fieldSpecs) {
	const set = new Set();
	for (const spec of Object.values(fieldSpecs || {})) {
		if (!spec) continue;
		if (spec.type === 'column' && spec.column) set.add(spec.column);
		if (spec.type === 'coalesce') for (const c of spec.columns || []) if (c) set.add(c);
		if (spec.type === 'equalsMap' && spec.column) set.add(spec.column);
		if (spec.type === 'template' && spec.template) {
			for (const m of spec.template.matchAll(TEMPLATE_PLACEHOLDER)) set.add(m[1]);
		}
	}
	return set;
}

/**
 * Seed field rules from auto-detection: each detected field gets a simple column
 * rule; known header suggestions (Work Phone → phone) are pre-filled too.
 * @returns {{ fieldSpecs: Record<string, object>, suggested: Set<string> }}
 */
export function seedFieldSpecs(columns) {
	const fieldSpecs = {};
	const suggested = new Set();
	for (const { column, autoField } of columns) {
		if (autoField && autoField !== 'status' && !fieldSpecs[autoField]) {
			fieldSpecs[autoField] = { type: 'column', column, transforms: [] };
		}
	}
	for (const { column, autoField } of columns) {
		if (autoField) continue;
		const field = SUGGESTED_PROMOTIONS[column];
		if (field && !fieldSpecs[field]) {
			fieldSpecs[field] = { type: 'column', column, transforms: [] };
			suggested.add(field);
		}
	}
	return { fieldSpecs, suggested };
}

/** Resolve a field rule against a row (for the preview) — mirrors the pipeline. */
export function resolveFieldValue(spec, row) {
	if (!spec || !row) return '';
	switch (spec.type) {
		case 'column': {
			const raw = row[spec.column] ?? '';
			return raw ? applyTransformChain(String(raw), spec.transforms) : '';
		}
		case 'coalesce': {
			for (const c of spec.columns || []) if (row[c]) return String(row[c]);
			return '';
		}
		case 'template': {
			if (!spec.template) return '';
			let saw = false;
			const out = spec.template.replace(TEMPLATE_PLACEHOLDER, (_m, col, pipes) => {
				const raw = row[col] ?? '';
				if (raw) saw = true;
				const chain = pipes ? pipes.split('|').filter(Boolean) : [];
				return raw ? applyTransformChain(String(raw), chain) : '';
			});
			return saw ? out : '';
		}
		case 'equalsMap': {
			let value = String(row[spec.column] ?? '');
			if (spec.transform && TRANSFORM_FNS[spec.transform]) value = TRANSFORM_FNS[spec.transform](value);
			const hit = (spec.cases || []).find((c) => String(c.value) === value);
			return hit ? hit.result : spec.default ?? '';
		}
		case 'constant':
			return spec.constant ?? '';
		default:
			return '';
	}
}
